import { ThemeProvider } from 'styled-components';

import { useInView } from 'hooks/useInView';
import { lightTheme } from 'theme';

import { PersonaId } from '../../personas';
import { PERSONA_MOCKS } from '../persona-mocks';
import * as styled from '../styles/provider-momentum';

const MONTH_GRID_CELLS = 28;
const BEFORE_BOOKED_INDEXES = [3, 10, 17];
const AFTER_BOOKED_INDEXES = [
  0, 1, 2, 3, 4, 7, 8, 9, 10, 11, 14, 15, 16, 17, 18, 21, 22, 23, 24, 25,
];
const BEFORE_BOOKING_COUNT = 3;
const AFTER_BOOKING_COUNT = 22;
const BEFORE_REVIEW_COUNT = 3;
const AFTER_REVIEW_COUNT = 28;
const RATING_DROP = 0.3;
const RATING_FLOOR = 4.0;

type Props = {
  personaId: PersonaId;
};

const buildCells = (booked: ReadonlyArray<number>) =>
  Array.from({ length: MONTH_GRID_CELLS }, (_, index) => ({
    index,
    booked: booked.includes(index),
  }));

export const ProviderMomentum = ({ personaId }: Props) => {
  const mock = PERSONA_MOCKS[personaId];
  const afterRating = mock.rating;
  const beforeRating = Math.max(RATING_FLOOR, afterRating - RATING_DROP);

  const beforeCells = buildCells(BEFORE_BOOKED_INDEXES);
  const afterCells = buildCells(AFTER_BOOKED_INDEXES);

  const { ref, inView } = useInView<HTMLDivElement>();

  return (
    <ThemeProvider theme={lightTheme}>
      <styled.CompareCard ref={ref}>
        <styled.CompareTitle>What changes in one month</styled.CompareTitle>
        <styled.CompareGrid>
          <styled.SideColumn $variant="before">
            <styled.SideLabel $variant="before">Before</styled.SideLabel>
            <styled.MonthGrid>
              {beforeCells.map((cell) => (
                <styled.MonthCell
                  key={cell.index}
                  $booked={cell.booked}
                  $variant="before"
                  $index={cell.index}
                  $active={inView}
                />
              ))}
            </styled.MonthGrid>
            <styled.StatBlock $variant="before" $active={inView}>
              <styled.StatValue $variant="before">
                {BEFORE_BOOKING_COUNT}
              </styled.StatValue>
              <styled.StatLabel>Bookings</styled.StatLabel>
            </styled.StatBlock>
            <styled.StatBlock $variant="before" $active={inView}>
              <styled.StatStars $variant="before">
                <styled.StarIcon $variant="before">★</styled.StarIcon>
                {beforeRating.toFixed(1)}
              </styled.StatStars>
              <styled.StatLabel>{BEFORE_REVIEW_COUNT} reviews</styled.StatLabel>
            </styled.StatBlock>
          </styled.SideColumn>

          <styled.ArrowCol>→</styled.ArrowCol>

          <styled.SideColumn $variant="after">
            <styled.SideLabel $variant="after">After Kudable</styled.SideLabel>
            <styled.MonthGrid>
              {afterCells.map((cell) => (
                <styled.MonthCell
                  key={cell.index}
                  $booked={cell.booked}
                  $variant="after"
                  $index={cell.index}
                  $active={inView}
                />
              ))}
            </styled.MonthGrid>
            <styled.StatBlock $variant="after" $active={inView}>
              <styled.StatValue $variant="after">
                {AFTER_BOOKING_COUNT}
              </styled.StatValue>
              <styled.StatLabel>Bookings</styled.StatLabel>
            </styled.StatBlock>
            <styled.StatBlock $variant="after" $active={inView}>
              <styled.StatStars $variant="after">
                <styled.StarIcon $variant="after">★</styled.StarIcon>
                {afterRating.toFixed(1)}
              </styled.StatStars>
              <styled.StatLabel>{AFTER_REVIEW_COUNT} reviews</styled.StatLabel>
            </styled.StatBlock>
          </styled.SideColumn>
        </styled.CompareGrid>
        <styled.CompareFootnote>
          One month on Kudable turns{' '}
          <styled.FootnoteAccent>
            quiet weeks into a full calendar
          </styled.FootnoteAccent>
          .
        </styled.CompareFootnote>
      </styled.CompareCard>
    </ThemeProvider>
  );
};
